// PartnerInactiveBillDetailsComponent.js
import React, { useState, useEffect } from "react";
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import LoadingSpinner from "../../../Loading/component";
import styles from "./PartnerInactiveBillDetails.module.css";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import dayWiseData from "../../../../assests/daysWiseData.svg";
import billsummery from "../../../../assests/billsummery.svg";
import chargeicon from "../../../../assests/chargeicon.svg";
import cancel from "../../../../assests/cancel.svg";
import leftArrow from "../../../../assests/leftArrow.svg";

const PartnerInactiveBillDetailsComponent = ({
  loading,
  chargesLoading,
  billData,
  payoutBillData,
  onGoBack,
  handleMiscCharge,
  chargeSummary,
  fetchChargeBillSummary,
  allCharges,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [showDeductionDetails, setShowDeductionDetails] = useState(false);
  const [calculatedEndDate, setCalculatedEndDate] = useState("");
  const [calculatedStartDate, setCalculatedStartDate] = useState("");
  const [selectedDayIndex, setSelectedDayIndex] = useState(null);
  const [selectedPayoutId, setSelectedPayoutId] = useState(null);
  const [selectedChargeId, setSelectedChargeId] = useState(null);
  const [activeChargeTab, setActiveChargeTab] = useState("bill");

  useEffect(() => {
    if (billData || payoutBillData) {
      const startDate = calculateStartDate();
      const endDate = calculateEndDate(startDate);
      setCalculatedStartDate(startDate);
      setCalculatedEndDate(endDate);
    }
  }, [billData, payoutBillData]);

  const getLastDateOfMonth = (dateString) => {
    try {
      if (
        !dateString ||
        dateString === "N/A" ||
        dateString === "null" ||
        dateString === "undefined"
      ) {
        return "N/A";
      }

      let date;
      if (dateString.includes("/")) {
        const [day, month, year] = dateString.split("/").map(Number);
        date = new Date(year, month - 1, day);
      } else if (dateString.includes("-")) {
        date = new Date(dateString);
      } else {
        return "N/A";
      }

      if (isNaN(date.getTime())) return "N/A";

      const lastDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      const formattedDay = String(lastDate.getDate()).padStart(2, "0");
      const formattedMonth = String(lastDate.getMonth() + 1).padStart(2, "0");
      const formattedYear = lastDate.getFullYear();

      return `${formattedDay}/${formattedMonth}/${formattedYear}`;
    } catch (error) {
      console.error("Error calculating last date of month:", error);
      return "N/A";
    }
  };

  const calculateStartDate = () => {
    if (
      billData?.startDate &&
      billData.startDate !== "null" &&
      billData.startDate !== "undefined" &&
      billData.startDate !== "N/A"
    ) {
      return formatDisplayDate(billData.startDate);
    }
    if (
      payoutBillData?.startDate &&
      payoutBillData.startDate !== "null" &&
      payoutBillData.startDate !== "undefined" &&
      payoutBillData.startDate !== "N/A"
    ) {
      return formatDisplayDate(payoutBillData.startDate);
    }
    return "N/A";
  };

  const calculateEndDate = (startDate) => {
    if (
      billData?.endDate &&
      billData.endDate !== "null" &&
      billData.endDate !== "undefined" &&
      billData.endDate !== "N/A"
    ) {
      return formatDisplayDate(billData.endDate);
    }
    if (startDate && startDate !== "N/A") {
      const monthEndDate = getLastDateOfMonth(startDate);
      if (monthEndDate !== "N/A") return monthEndDate;
    }
    if (
      payoutBillData?.endDate &&
      payoutBillData.endDate !== "null" &&
      payoutBillData.endDate !== "undefined" &&
      payoutBillData.endDate !== "N/A"
    ) {
      return formatDisplayDate(payoutBillData.endDate);
    }
    return "N/A";
  };

  const formatDisplayDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleString("default", { month: "short" });
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch {
      return dateString;
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!billData)
    return (
      <div className={styles.pibdNoData}>No bill data available.</div>
    );

  const handleViewBillChargeClick = (charge) => {
    setSelectedCharge(charge);
    setSelectedChargeId(charge.id);
    setActiveChargeTab("bill");
    setOpenDialog(true);
  };

  const handleViewPartnerChargeClick = (charge) => {
    setSelectedCharge(charge);
    setSelectedChargeId(charge.id);
    setActiveChargeTab("partner");
    setOpenDialog(true);
  };

  const totalTransactionalAmount = chargeSummary?.transactionalCharges?.reduce(
    (acc, charge) => acc + (charge.amount || 0),
    0,
  );

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "₹ 0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(amount)
      .replace("₹", "₹ ");
  };

  const formatChargeTypeLabel = (chargeType) => {
    if (!chargeType) return "N/A";
    if (chargeType === "raas_fee") return "RAAS Fee (%)";
    if (chargeType === "ps_fee") return "PS Fee (%)";
    if (chargeType === "saas_ik") return "SAAS IK";
    if (chargeType === "rental_ik") return "Rental IK";
    if (chargeType === "misc_charge") return "Miscellaneous";
    if (chargeType === "debit") return "Debit";
    return chargeType
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatValueType = (valueType) => {
    if (!valueType) return "N/A";
    if (valueType === "amount_percentage") return "Percentage (%)";
    if (valueType === "amount_flat") return "Flat Amount";
    if (valueType === "fixed") return "Fixed";
    if (valueType === "percentage") return "Percentage (%)";
    return valueType.replace(/_/g, " ");
  };

  const totalFixedAmount = chargeSummary?.fixedCharges?.reduce(
    (acc, charge) => acc + (charge.amount || 0),
    0,
  );

  const totalDeduction =
    (totalTransactionalAmount || 0) + (totalFixedAmount || 0);

  const selectedDayNetPayable =
    selectedDayIndex !== null && payoutBillData?.payouts?.[selectedDayIndex]
      ? payoutBillData.payouts[selectedDayIndex].netPayable || 0
      : 0;

  const netDeduction = totalDeduction + selectedDayNetPayable;

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCharge(null);
    setSelectedChargeId(null);
  };

  const handleDeductionClick = (payoutId, totalDeductions, dayIndex) => {
    setSelectedDayIndex(dayIndex);
    setSelectedPayoutId(payoutId);
    if (totalDeductions >= 0) {
      fetchChargeBillSummary(payoutId);
      setShowDeductionDetails(true);
    }
  };

  const getChargeDetailsById = (chargeId) => {
    if (!allCharges || !Array.isArray(allCharges)) return null;
    return allCharges.find((charge) => charge.id === chargeId);
  };

  const selectedChargeDetails = getChargeDetailsById(selectedChargeId);

  // ⭐ Bill charges array
  const billCharges = billData?.charges || [];
  // ⭐ Partner charges array
  const partnerCharges = Array.isArray(allCharges) ? allCharges : [];

  return (
    <div className={styles.pibdLayout}>
      <Sidebar />
      <div className={styles.pibdContainer}>
        {/* Header Section */}
        <div className={styles.pibdHeaderWrapper}>
          <button className={styles.pibdBackBtn} onClick={onGoBack}>
            <img src={leftArrow} alt="Back" />
          </button>
          <h2 className={styles.pibdTitle}>
            Partner - {billData.name}{" "}
            <span className={styles.pibdTitleId}>
              (Owner ID: {billData.franchiseeId})
            </span>
          </h2>
        </div>

        {/* Bill ID Banner */}
        <div className={styles.pibdBanner}>
          <div className={styles.pibdBannerItem}>
            <span className={styles.pibdBannerLabel}>Bill ID</span>
            <span className={styles.pibdBannerValue}>{billData.billId}</span>
          </div>
          <div className={styles.pibdBannerItem}>
            <span className={styles.pibdBannerLabel}>Total VM</span>
            <span className={styles.pibdBannerValue}>
              {payoutBillData?.vmCount || 0}
            </span>
          </div>
          <div className={styles.pibdBannerRight}>
            <img src={billsummery} alt="Calendar" />
            <span className={styles.pibdBannerLabel}>Start Date:</span>
            <span className={styles.pibdBannerValue}>
              {calculatedStartDate}
            </span>
            <span className={styles.pibdBannerLabel}>End Date:</span>
            <span className={styles.pibdBannerValue}>{calculatedEndDate}</span>
          </div>
        </div>

        {/* ============================================================
            COMBINED CHARGES SECTION
            ============================================================ */}
        <div className={styles.pibdChargesSection}>
          <div className={styles.pibdChargesHeader}>
            <h4 className={styles.pibdChargesTitle}>
              <span className={styles.pibdIcon}>⚡</span> Charges
            </h4>
            {billData.status !== "CLOSED" && (
              <Button
                variant="outlined"
                className={styles.pibdAddChargeBtn}
                onClick={handleMiscCharge}
              >
                + Add Misc. Charge
              </Button>
            )}
          </div>

          {chargesLoading ? (
            <div className={styles.pibdChargesLoading}>
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {/* ---------- SUB SECTION: BILL CHARGES ---------- */}
              <div className={styles.pibdChargesSubsection}>
                <div className={styles.pibdChargesSubheader}>
                  <span className={styles.pibdChargesSubheaderIcon}>📄</span>
                  <span className={styles.pibdChargesSubheaderText}>
                    Charges Applied on this Bill
                  </span>
                  <span className={styles.pibdChargesSubheaderCount}>
                    {billCharges.length}
                  </span>
                </div>

                <div className={styles.pibdChargesContainer}>
                  {billCharges.length > 0 ? (
                    billCharges.map((charge, i) => (
                      <div
                        className={styles.pibdChargeCard}
                        key={`bill-${charge.id}-${i}`}
                      >
                        {charge.chargeType === "misc_charge" && (
                          <div className={styles.pibdMiscBadge}>M</div>
                        )}

                        <div className={styles.pibdChargeInfo}>
                          <div className={styles.pibdChargeRow}>
                            <span className={styles.pibdChargeLabel}>ID:</span>
                            <span className={styles.pibdChargeValue}>
                              {charge.id}
                            </span>
                          </div>
                          <div className={styles.pibdChargeRow}>
                            <span className={styles.pibdChargeLabel}>
                              Name:
                            </span>
                            <span
                              className={styles.pibdChargeValue}
                              title={charge.name || charge.chargeType}
                            >
                              {charge.name ||
                                formatChargeTypeLabel(charge.chargeType)}
                            </span>
                          </div>
                          <div className={styles.pibdChargeRow}>
                            <span className={styles.pibdChargeLabel}>
                              Type:
                            </span>
                            <span
                              className={`${styles.pibdChargeValue} ${styles.pibdChargeValueBadge}`}
                            >
                              {formatChargeTypeLabel(charge.chargeType)}
                            </span>
                          </div>
                          <div className={styles.pibdChargeRow}>
                            <span className={styles.pibdChargeLabel}>
                              Amount:
                            </span>
                            <span
                              className={`${styles.pibdChargeValue} ${styles.pibdChargeValueAmount}`}
                            >
                              {charge.valueType === "amount_percentage" ||
                              charge.valueType === "percentage"
                                ? `${charge.amount}%`
                                : formatCurrency(charge.amount || 0)}
                            </span>
                          </div>
                        </div>

                        <IconButton
                          className={styles.pibdChargeViewBtn}
                          onClick={() => handleViewBillChargeClick(charge)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))
                  ) : (
                    <div className={styles.pibdNoCharges}>
                      No charges applied on this bill
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.pibdChargesDivider} />

              {/* ---------- SUB SECTION: PARTNER CHARGES ---------- */}
              <div className={styles.pibdChargesSubsection}>
                <div className={styles.pibdChargesSubheader}>
                  <span className={styles.pibdChargesSubheaderIcon}>⚙️</span>
                  <span className={styles.pibdChargesSubheaderText}>
                    Partner Charges Configuration
                  </span>
                  <span className={styles.pibdChargesSubheaderCount}>
                    {partnerCharges.length}
                  </span>
                </div>

                <div className={styles.pibdChargesContainer}>
                  {partnerCharges.length > 0 ? (
                    partnerCharges.map((charge, i) => (
                      <div
                        className={`${styles.pibdChargeCard} ${styles.pibdPartnerChargeCard}`}
                        key={`partner-${charge.id}-${i}`}
                      >
                        <div className={styles.pibdChargeInfo}>
                          <div className={styles.pibdChargeRow}>
                            <span className={styles.pibdChargeLabel}>ID:</span>
                            <span className={styles.pibdChargeValue}>
                              {charge.id}
                            </span>
                          </div>
                          <div className={styles.pibdChargeRow}>
                            <span className={styles.pibdChargeLabel}>
                              Name:
                            </span>
                            <span
                              className={styles.pibdChargeValue}
                              title={charge.name || "N/A"}
                            >
                              {charge.name || "N/A"}
                            </span>
                          </div>
                          <div className={styles.pibdChargeRow}>
                            <span className={styles.pibdChargeLabel}>
                              Value:
                            </span>
                            <span
                              className={`${styles.pibdChargeValue} ${styles.pibdChargeValueAmount}`}
                            >
                              {charge.valueType === "amount_percentage"
                                ? `${charge.value}%`
                                : formatCurrency(Number(charge.value) || 0)}
                            </span>
                          </div>
                        </div>

                        <IconButton
                          className={styles.pibdChargeViewBtn}
                          onClick={() => handleViewPartnerChargeClick(charge)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))
                  ) : (
                    <div className={styles.pibdNoCharges}>
                      No partner charges configured
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* ============================================================
            CHARGE DETAILS DIALOG
            ============================================================ */}
        <Dialog
          open={openDialog}
          className={styles.pibdChargeDialog}
          PaperProps={{
            style: {
              borderRadius: "16px",
              padding: "8px",
              minWidth: "450px",
              maxWidth: "600px",
            },
          }}
        >
          <DialogTitle className={styles.pibdDialogTitle}>
            <div className={styles.pibdDialogTitleContent}>
              <img src={chargeicon} alt="Charge Icon" />
              <div>
                <Typography variant="h5" className={styles.pibdDialogTitleText}>
                  {activeChargeTab === "bill"
                    ? selectedCharge?.name ||
                      formatChargeTypeLabel(selectedCharge?.chargeType) ||
                      "Charge Details"
                    : selectedChargeDetails?.name ||
                      selectedCharge?.name ||
                      "Charge Details"}
                </Typography>
                <Typography
                  variant="subtitle2"
                  className={styles.pibdDialogSubtitle}
                >
                  {activeChargeTab === "bill"
                    ? "Bill Charge"
                    : "Partner Configuration Charge"}{" "}
                  · Charge ID:{" "}
                  {selectedChargeDetails?.id || selectedCharge?.id}
                </Typography>
              </div>
            </div>
          </DialogTitle>

          <DialogContent className={styles.pibdDialogContent}>
            <div className={styles.pibdChargeDetailsGrid}>
              {activeChargeTab === "bill" && selectedCharge ? (
                <>
                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>Charge ID</span>
                    <span className={styles.pibdDetailValue}>
                      {selectedCharge.id}
                    </span>
                  </div>

                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>Charge Name</span>
                    <span className={styles.pibdDetailValue}>
                      {selectedCharge.name || "N/A"}
                    </span>
                  </div>

                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>Charge Type</span>
                    <span className={styles.pibdDetailValue}>
                      {formatChargeTypeLabel(selectedCharge.chargeType)}
                    </span>
                  </div>

                  <div
                    className={`${styles.pibdDetailItem} ${styles.pibdDetailFull}`}
                  >
                    <span className={styles.pibdDetailLabel}>Description</span>
                    <span className={styles.pibdDetailValue}>
                      {selectedCharge.description ||
                        "No description available"}
                    </span>
                  </div>

                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>Value Type</span>
                    <span className={styles.pibdDetailValue}>
                      {formatValueType(selectedCharge.valueType)}
                    </span>
                  </div>

                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>Amount</span>
                    <span
                      className={`${styles.pibdDetailValue} ${styles.pibdAmountValue}`}
                    >
                      {selectedCharge.valueType === "amount_percentage" ||
                      selectedCharge.valueType === "percentage"
                        ? `${selectedCharge.amount}%`
                        : formatCurrency(selectedCharge.amount || 0)}
                    </span>
                  </div>

                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>
                      Minimum Value
                    </span>
                    <span className={styles.pibdDetailValue}>
                      {selectedCharge.valueMin !== null &&
                      selectedCharge.valueMin !== undefined
                        ? selectedCharge.valueType === "amount_percentage"
                          ? `${selectedCharge.valueMin}%`
                          : formatCurrency(selectedCharge.valueMin)
                        : "Not Set"}
                    </span>
                  </div>

                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>
                      Maximum Value
                    </span>
                    <span className={styles.pibdDetailValue}>
                      {selectedCharge.valueMax !== null &&
                      selectedCharge.valueMax !== undefined
                        ? selectedCharge.valueType === "amount_percentage"
                          ? `${selectedCharge.valueMax}%`
                          : formatCurrency(selectedCharge.valueMax)
                        : "Not Set"}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>Charge ID</span>
                    <span className={styles.pibdDetailValue}>
                      {selectedChargeDetails?.id || selectedCharge?.id}
                    </span>
                  </div>

                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>Charge Name</span>
                    <span className={styles.pibdDetailValue}>
                      {selectedChargeDetails?.name ||
                        selectedCharge?.name ||
                        "N/A"}
                    </span>
                  </div>

                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>Charge Type</span>
                    <span className={styles.pibdDetailValue}>
                      {selectedChargeDetails?.chargeType ||
                        selectedCharge?.chargeType ||
                        "N/A"}
                    </span>
                  </div>

                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>
                      Deduction Type ID
                    </span>
                    <span className={styles.pibdDetailValue}>
                      {selectedChargeDetails?.deductionTypeId || "N/A"}
                    </span>
                  </div>

                  <div
                    className={`${styles.pibdDetailItem} ${styles.pibdDetailFull}`}
                  >
                    <span className={styles.pibdDetailLabel}>Description</span>
                    <span className={styles.pibdDetailValue}>
                      {selectedChargeDetails?.description ||
                        "No description available"}
                    </span>
                  </div>

                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>Value Type</span>
                    <span className={styles.pibdDetailValue}>
                      {selectedChargeDetails?.valueType || "N/A"}
                    </span>
                  </div>

                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>
                      Value Amount
                    </span>
                    <span
                      className={`${styles.pibdDetailValue} ${styles.pibdAmountValue}`}
                    >
                      {selectedChargeDetails?.value
                        ? selectedChargeDetails.valueType?.includes(
                            "percentage",
                          )
                          ? `${selectedChargeDetails.value}%`
                          : formatCurrency(
                              Number(selectedChargeDetails.value) || 0,
                            )
                        : formatCurrency(selectedCharge?.amount || 0)}
                    </span>
                  </div>

                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>
                      Minimum Value
                    </span>
                    <span className={styles.pibdDetailValue}>
                      {selectedChargeDetails?.valueMin
                        ? selectedChargeDetails.valueType?.includes(
                            "percentage",
                          )
                          ? `${selectedChargeDetails.valueMin}%`
                          : formatCurrency(
                              Number(selectedChargeDetails.valueMin) || 0,
                            )
                        : "Not Set"}
                    </span>
                  </div>

                  <div className={styles.pibdDetailItem}>
                    <span className={styles.pibdDetailLabel}>
                      Maximum Value
                    </span>
                    <span className={styles.pibdDetailValue}>
                      {selectedChargeDetails?.valueMax
                        ? selectedChargeDetails.valueType?.includes(
                            "percentage",
                          )
                          ? `${selectedChargeDetails.valueMax}%`
                          : formatCurrency(
                              Number(selectedChargeDetails.valueMax) || 0,
                            )
                        : "Not Set"}
                    </span>
                  </div>
                </>
              )}
            </div>
          </DialogContent>

          <DialogActions className={styles.pibdDialogActions}>
            <Button
              onClick={handleCloseDialog}
              variant="contained"
              className={styles.pibdDialogCloseBtn}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* ============================================================
            SUMMARY SECTION (View Past Bills REMOVED)
            ============================================================ */}
        <div className={styles.pibdSummarySection}>
          <div className={styles.pibdSummaryHeader}>
            <h4 className={styles.pibdSummaryTitle}>
              <span className={styles.pibdIcon}>📅</span> Bill Summary -{" "}
              <span className={styles.pibdDateRange}>
                {calculatedStartDate}
              </span>
              <span className={styles.pibdDateSeparator}>to</span>
              <span className={styles.pibdDateRange}>{calculatedEndDate}</span>
              <span
                className={`${styles.pibdStatusBadge} ${
                  styles[
                    `pibdStatus${billData.status?.charAt(0)}${billData.status
                      ?.slice(1)
                      .toLowerCase()}`
                  ] || ""
                }`}
              >
                {billData.status}
              </span>
            </h4>
            {/* ⭐ View Past Bills REMOVED — ye inactive/past bill hai */}
          </div>

          <div className={styles.pibdSummaryCards}>
            <div className={styles.pibdSummaryCard}>
              <div className={styles.pibdCardLabel}>Total Gross Sale</div>
              <strong className={styles.pibdCardValue}>
                {formatCurrency(payoutBillData?.totalGrossSale || 0)}
              </strong>
            </div>
            <div className={styles.pibdSummaryCard}>
              <div className={styles.pibdCardLabel}>Total Fix Deduction</div>
              <strong className={styles.pibdCardValue}>
                {formatCurrency(payoutBillData?.totalFixedDeductions || 0)}
              </strong>
            </div>
            <div className={styles.pibdSummaryCard}>
              <div className={styles.pibdCardLabel}>
                Total Transactional Deduction
              </div>
              <strong className={styles.pibdCardValue}>
                {formatCurrency(
                  payoutBillData?.totalTransactionalDeductions || 0,
                )}
              </strong>
            </div>
            <div className={styles.pibdSummaryCard}>
              <div className={styles.pibdCardLabel}>Net Payout</div>
              <strong
                className={`${styles.pibdCardValue} ${
                  (payoutBillData?.totalNetPayable || 0) < 0
                    ? styles.pibdNegative
                    : styles.pibdPositive
                }`}
              >
                {formatCurrency(payoutBillData?.totalNetPayable || 0)}
              </strong>
            </div>
          </div>
        </div>

        {/* ============================================================
            DAY-WISE TABLE
            ============================================================ */}
        <div className={styles.pibdTableSection}>
          <h4 className={styles.pibdTableTitle}>
            <span className={styles.pibdIcon}>📊</span> Day Wise Payout Details
          </h4>
          <div className={styles.pibdTableWrapper}>
            <table className={styles.pibdPayoutTable}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Settlement ID</th>
                  <th>Total Gross Sale</th>
                  <th>Net Payout</th>
                  <th>Total Deduction</th>
                </tr>
              </thead>
              <tbody>
                {payoutBillData && payoutBillData?.payouts?.length > 0 ? (
                  payoutBillData.payouts.map((day, i) => (
                    <tr
                      key={i}
                      className={
                        selectedDayIndex === i ? styles.pibdRowSelected : ""
                      }
                    >
                      <td>{formatDisplayDate(day.date)}</td>
                      <td>{day.payoutId}</td>
                      <td>{formatCurrency(day.revenueToday || 0)}</td>
                      <td
                        className={
                          day.netPayable < 0
                            ? styles.pibdNegativeValue
                            : styles.pibdPositiveValue
                        }
                      >
                        {formatCurrency(day.netPayable || 0)}
                      </td>
                      <td>
                        <span
                          className={styles.pibdDeductionLink}
                          onClick={() => {
                            handleDeductionClick(
                              day.payoutId,
                              day.netPayable < 0
                                ? day.revenueToday
                                : day.totalDeductions,
                              i,
                            );
                          }}
                        >
                          {formatCurrency(
                            day.netPayable < 0
                              ? day.revenueToday
                              : day.totalDeductions,
                          )}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className={styles.pibdNoData}>
                      No payout data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ============================================================
            DEDUCTION DETAILS DIALOG
            ============================================================ */}
        <Dialog
          open={showDeductionDetails}
          maxWidth="md"
          fullWidth
          onClose={() => setShowDeductionDetails(false)}
          PaperProps={{
            style: {
              borderRadius: "16px",
              maxHeight: "90vh",
              overflow: "hidden",
            },
          }}
        >
          <div className={styles.pibdDeductionHeader}>
            <DialogTitle className={styles.pibdDeductionTitle}>
              {chargeSummary?.date
                ? new Date(chargeSummary.date).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : "N/A"}
              {selectedPayoutId && ` (Payout ID: ${selectedPayoutId})`}
            </DialogTitle>
            <IconButton
              onClick={() => setShowDeductionDetails(false)}
              className={styles.pibdDeductionClose}
            >
              <img src={cancel} alt="Close" />
            </IconButton>
          </div>

          <DialogContent className={styles.pibdDeductionContent}>
            {!chargeSummary ? (
              <LoadingSpinner />
            ) : (
              <div className={styles.pibdDeductionWrapper}>
                {/* Partner Info */}
                <div className={styles.pibdPartnerInfo}>
                  <div className={styles.pibdPartnerInfoRow}>
                    <div>
                      <span className={styles.pibdInfoLabel}>
                        Partner Name
                      </span>
                      <span className={styles.pibdInfoValue}>
                        {chargeSummary?.name || "N/A"} (
                        {chargeSummary?.type || "N/A"})
                      </span>
                      <br />
                      <span className={styles.pibdInfoLabel}>Cohort ID</span>
                      <span className={styles.pibdInfoValue}>
                        {chargeSummary?.cohortId || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className={styles.pibdInfoLabel}>Partner Id</span>
                      <span className={styles.pibdInfoValue}>
                        {chargeSummary?.franchiseeId || "N/A"}
                      </span>
                      <br />
                      <span className={styles.pibdInfoLabel}>Total VM</span>
                      <span className={styles.pibdInfoValue}>
                        {chargeSummary?.vmCount || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Transactional Charges Table */}
                <h4 className={styles.pibdTableSubtitle}>
                  Charge Summary (Transactional)
                </h4>
                <TableContainer className={styles.pibdTableContainer}>
                  <Table size="small">
                    <TableHead>
                      <TableRow className={styles.pibdTableHeaderRow}>
                        <TableCell
                          className={styles.pibdTableHeaderCell}
                          style={{ width: "20%" }}
                        >
                          Deduction ID
                        </TableCell>
                        <TableCell
                          className={styles.pibdTableHeaderCell}
                          style={{ width: "50%" }}
                        >
                          Description
                        </TableCell>
                        <TableCell
                          className={styles.pibdTableHeaderCell}
                          align="right"
                          style={{ width: "30%" }}
                        >
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chargeSummary?.transactionalCharges?.length > 0 ? (
                        chargeSummary.transactionalCharges.map((charge) => (
                          <TableRow
                            key={`transactional-${charge.id}`}
                            className={styles.pibdTableBodyRow}
                          >
                            <TableCell>{charge.id}</TableCell>
                            <TableCell>
                              {charge.chargeType === "raas_fee"
                                ? "RAAS Fee(%)"
                                : charge.chargeType === "ps_fee"
                                  ? "PS Fee(%)"
                                  : charge.chargeType}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(charge.amount)}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            className={styles.pibdNoData}
                          >
                            No transactional charges
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow className={styles.pibdTableTotalRow}>
                        <TableCell colSpan={2}>% Charge Total</TableCell>
                        <TableCell align="right">
                          {formatCurrency(totalTransactionalAmount || 0)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Fixed Charges Table */}
                <h4 className={styles.pibdTableSubtitle}>
                  Charge Summary (Fixed)
                </h4>
                <TableContainer className={styles.pibdTableContainer}>
                  <Table size="small">
                    <TableHead>
                      <TableRow className={styles.pibdTableHeaderRow}>
                        <TableCell
                          className={styles.pibdTableHeaderCell}
                          style={{ width: "20%" }}
                        >
                          Deduction ID
                        </TableCell>
                        <TableCell
                          className={styles.pibdTableHeaderCell}
                          style={{ width: "50%" }}
                        >
                          Description
                        </TableCell>
                        <TableCell
                          className={styles.pibdTableHeaderCell}
                          align="right"
                          style={{ width: "30%" }}
                        >
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chargeSummary?.fixedCharges?.length > 0 ? (
                        chargeSummary.fixedCharges.map((charge) => (
                          <TableRow
                            key={`fixed-${charge.id}`}
                            className={styles.pibdTableBodyRow}
                          >
                            <TableCell>{charge.id}</TableCell>
                            <TableCell>
                              {charge.chargeType === "saas_ik"
                                ? "SAAS IK"
                                : charge.chargeType === "rental_ik"
                                  ? "Rental IK"
                                  : charge.chargeType === "misc_charge"
                                    ? "Miscellaneous"
                                    : charge.chargeType}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(charge.amount)}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            className={styles.pibdNoData}
                          >
                            No fixed charges
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow className={styles.pibdTableTotalRow}>
                        <TableCell colSpan={2}>Fix Charge Total</TableCell>
                        <TableCell align="right">
                          {formatCurrency(totalFixedAmount || 0)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Total Deduction */}
                <div className={styles.pibdTotalDeduction}>
                  <span>Total Deduction (Transactional + Fixed):</span>
                  <span className={styles.pibdTotalDeductionValue}>
                    {formatCurrency(
                      (chargeSummary?.totalTransactionalDeductions || 0) +
                        (chargeSummary?.totalFixedDeductions || 0),
                    )}
                  </span>
                </div>

                {/* Net Deduction */}
                <div className={styles.pibdNetDeduction}>
                  <span>Net Deduction (Total Deduction + Net Payout):</span>
                  <div className={styles.pibdNetDeductionRight}>
                    <span
                      className={`${styles.pibdNetDeductionValue} ${
                        netDeduction < 0
                          ? styles.pibdNegative
                          : styles.pibdPositive
                      }`}
                    >
                      {formatCurrency(netDeduction)}
                    </span>
                    <span className={styles.pibdNetPayoutInfo}>
                      (Net Payout: {formatCurrency(selectedDayNetPayable)})
                    </span>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PartnerInactiveBillDetailsComponent;